/**
 *
 * Adjustments
 *
 */

import React, { memo, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';

import {
  DATE_AND_TIME_FORMAT,
  DEFAULT,
  DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_OPTIONS,
  PLACEHOLDER_TEXT,
  TX_STATUS_CLASSNAME,
} from 'utils/constants';
import {
  addCurrencySymbol,
  roundOffNumber,
  titleCase,
  exportToCSV as exportToCSVFn,
  copyToClipboard,
  removeCharacter,
} from 'utils/common';
import moment from 'moment';
import { Button, Conditional, Text } from '@cashfree-intl/coherent';

import PaginatedTable from 'components/PaginatedTable';
import { formatColumns } from 'components/PaginatedTable/helpers';

import saga from './saga';
import reducer from './reducer';
import makeSelectAdjustments from './selectors';
import {
  HEADINGS,
  ADJUST_BALANCE,
  EASY_SPLIT_REFUNDS,
  EASY_SPLIT_ADJUSTMENTS,
  REFUND_STATUS_MAPPER,
} from './constants';
import { resetTwoFactorAuthentication as resetTwoFactorAuthenticationValue } from '../Toast/actions';
import { twoFactorAuthenticationSelector } from '../Toast/selectors';
import {
  resetFiltersAction,
  resetPageAction,
  searchAdjustmentsAction,
  updateFiltersAction,
  vendorDataByIdAction,
  updateTableDataAction,
  adjustBalanceAction,
  enableLoaderAction,
  exportToCSVAction,
} from './actions';

import CustomIcon from '../../components/CustomIcons';
import { DashboardFilters } from '../DashboardFilters';
import AdjustBalanceModal from './components/AdjustBalanceModal';
import { EASY_SPLIT_KNOW_MORE_LINK } from '../EasySplit/constants';
import { StyledSubHeader } from './styled';

const renderWithCopyToClipboard = value => (
  <div className="flex copy-to-clipboard-wrapper">
    <span className="table-content">{value || PLACEHOLDER_TEXT}</span>
    <Conditional if={value && value !== 'N/A'}>
      <CustomIcon
        componentName="COPY_TO_CLIPBOARD"
        handleClick={copyToClipboard(value)}
      />
    </Conditional>
  </div>
);
export function Adjustments({
  searchAdjustments,
  menuSelected,
  resetFilters,
  updateFilters,
  resetPage,
  vendorDataById,
  updateTableData,
  adjustBalance,
  enableLoader,
  exportToCSV,
  adjustments,
  twoFactorAuthentication,
  resetTwoFactorAuthentication,
}) {
  useInjectReducer({ key: 'adjustments', reducer });
  useInjectSaga({ key: 'adjustments', saga });

  const {
    isLoading,
    filters,
    filtersForDisplay,
    hasPrev,
    hasNext,
    currentPageRowData,
    data,
  } = adjustments;

  const [searchParams] = useSearchParams();

  const headings = HEADINGS[menuSelected];
  const activeNavigation = DEFAULT;

  const [openModalName, resetModalName] = useState('');
  const [dataForExport, setDataForExport] = useState({});

  useEffect(() => {
    getTableData(filters);
  }, [filters, menuSelected, searchParams.get('env')]);

  const getTableData = (f = filters) => {
    enableLoader();

    const { appliedFilters, pageNo, pageSize } = f;

    const formattedFilterObj = {
      ...appliedFilters,
      pageNo: pageNo - 1,
      pageSize,
    };

    if (menuSelected === EASY_SPLIT_REFUNDS)
      formattedFilterObj.eventType = 'REFUND';

    if (
      appliedFilters.search &&
      appliedFilters.search.type &&
      appliedFilters.search.value
    ) {
      const {
        search: { type, value },
      } = appliedFilters;
      formattedFilterObj[type] = value;
      delete formattedFilterObj.search;
    }

    setDataForExport({
      options: {
        ...formattedFilterObj,
        isDownloaderRequest: true,
      },
      cb: exportToCSVFn,
    });

    searchAdjustments({
      options: formattedFilterObj,
    });
  };

  useEffect(() => {
    if (!isLoading) {
      const totalCount = data.length;
      const pageData = JSON.parse(JSON.stringify(data));
      let formattedData = [];
      switch (menuSelected) {
        case EASY_SPLIT_REFUNDS: {
          formattedData = pageData.map(rowData => ({
            ...rowData,
            initiatedAt:
              moment(rowData.refundAddedOn).format(DATE_AND_TIME_FORMAT) ||
              PLACEHOLDER_TEXT,
            orderId: () => renderWithCopyToClipboard(rowData.orderId),
            refundAmount: () => (
              <span>
                {addCurrencySymbol(roundOffNumber(rowData.refundAmount))}
              </span>
            ),
            refundId: () => renderWithCopyToClipboard(rowData.merchantRefundId),
            cfRefundId: () => renderWithCopyToClipboard(rowData.eventId),
            refundARN: () =>
              rowData.arn ? (
                <span className="table-content mw-100">
                  {removeCharacter(rowData.arn, '_', ' ')}
                </span>
              ) : (
                PLACEHOLDER_TEXT
              ),
            phoneNumber: rowData.customerPhone
              ? rowData.customerPhone
              : PLACEHOLDER_TEXT,
            paymentMode: () => (
              <div className="flex copy-to-clipboard-wrapper">
                <span className="table-content mw-100">
                  {removeCharacter(rowData.paymentMode, '_', ' ')}
                </span>
              </div>
            ),
            refundStatus: () => {
              const refStatus =
                REFUND_STATUS_MAPPER[rowData.refundStatus] || 'PENDING';
              return (
                <div className="flex copy-to-clipboard-wrapper">
                  <span className="table-content mw-100">
                    <span
                      className={`w-600 ${TX_STATUS_CLASSNAME[refStatus] ||
                        ''}`}
                    >
                      {titleCase(refStatus)}
                    </span>
                  </span>
                </div>
              );
            },
          }));
          break;
        }
        case EASY_SPLIT_ADJUSTMENTS: {
          formattedData = pageData.map(rowData => ({
            ...rowData,
            createdAt:
              moment(rowData.eventTime).format(DATE_AND_TIME_FORMAT) ||
              PLACEHOLDER_TEXT,
            adjustmentId: () => renderWithCopyToClipboard(rowData.eventId),
            creditDebit: () =>
              rowData.adjustmentType === 'C' ? 'Credit' : 'Debit',
            transferAmount: () => (
              <span>
                {addCurrencySymbol(roundOffNumber(rowData.eventAmount))}
              </span>
            ),
            remark: rowData.remarks ? rowData.remarks : PLACEHOLDER_TEXT,
          }));
          break;
        }
        default:
          break;
      }
      const hasPrevEnabled = filters.pageNo !== 1;
      const hasNextEnabled = totalCount >= filters.pageSize;

      updateTableData({
        totalCount,
        currentPageRowData: formattedData,
        hasPrevEnabled,
        hasNextEnabled,
      });
    }
  }, [isLoading, data]);

  useEffect(() => {
    if (twoFactorAuthentication) {
      getTableData();
      resetTwoFactorAuthentication(false);
    }
    return () => resetFilters();
  }, [twoFactorAuthentication, menuSelected]);

  const onPageChange = type => {
    const options = { ...filters, filtersForDisplay };
    switch (type) {
      case 'NEXT':
        options.pageNo += 1;
        break;
      case 'PREV':
        options.pageNo -= 1;
        break;
      default:
        break;
    }
    updateFilters(options);
  };

  const onLimitChange = (e, limitChangeData) => {
    const options = {
      ...filters,
      pageNo: DEFAULT_CURRENT_PAGE,
      pageSize: parseInt(limitChangeData.value, 10),
      filtersForDisplay,
    };
    updateFilters(options);
  };

  // eslint-disable-next-line no-unused-vars
  const handleAdjustBalanceClick = ({ modalName }) => e => {
    // if (e) e.stopPropagation();
    resetModalName(modalName);
  };

  const handleExportButtonClick = () => {
    enableLoader();
    exportToCSV(dataForExport);
  };

  const onAdjustBalanceSubmit = (payload, actions) => {
    adjustBalance({
      data: payload,
      successCb: () => {
        closeModal();
        getTableData();
      },
      errorCb: () => actions.setSubmitting(false),
    });
  };

  const closeModal = () => {
    resetModalName('');
  };

  const FORMATTED_COLUMN_ID = formatColumns(headings);

  return (
    <div className="adjustments">
      <StyledSubHeader>
        <Text className="text-light mt-0">
          All vendors are shown here.{' '}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href={EASY_SPLIT_KNOW_MORE_LINK} target="_blank">
            Know more
          </a>
        </Text>
      </StyledSubHeader>
      <div className="filters-wrapper">
        <DashboardFilters
          menuSelected={menuSelected}
          filters={filters}
          filtersForDisplay={filtersForDisplay}
          activeNavigation={activeNavigation}
          getTableDataFn={getTableData}
          resetFilterFn={resetFilters}
          updateFilterFn={updateFilters}
          resetPageFn={resetPage}
        />
        <div className="tab-cta-wrapper">
          <div>
            <Conditional if={menuSelected === EASY_SPLIT_REFUNDS}>
              <Button
                primary
                onClick={handleExportButtonClick}
                classNam="cta-item"
                disabled={currentPageRowData.length === 0 && !hasPrev}
                data-event-name="Primary_Button_Export"
              >
                Export
              </Button>
            </Conditional>
            <Conditional if={menuSelected === EASY_SPLIT_ADJUSTMENTS}>
              <Button
                primary
                onClick={handleAdjustBalanceClick({
                  modalName: ADJUST_BALANCE,
                  rowData: {},
                })}
                data-event-name="Primary_Button_AdjustBalance"
              >
                Adjust Balance
              </Button>
            </Conditional>
          </div>
        </div>
      </div>

      <PaginatedTable
        data={currentPageRowData}
        tableHeading={FORMATTED_COLUMN_ID}
        limit={filters.pageSize}
        limitOptions={DEFAULT_LIMIT_OPTIONS}
        hasPrev={hasPrev}
        hasNext={hasNext}
        currentPage={filters.pageNo}
        currentPageCount={currentPageRowData.length}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
        fetching={isLoading}
      />

      <Conditional if={openModalName === ADJUST_BALANCE}>
        <AdjustBalanceModal
          onClose={closeModal}
          onSubmit={onAdjustBalanceSubmit}
          vendorDataById={vendorDataById}
        />
      </Conditional>
    </div>
  );
}

Adjustments.propTypes = {
  searchAdjustments: PropTypes.func.isRequired,
  menuSelected: PropTypes.any.isRequired,
  resetFilters: PropTypes.func.isRequired,
  updateFilters: PropTypes.func.isRequired,
  resetPage: PropTypes.func.isRequired,
  adjustments: PropTypes.object.isRequired,
  vendorDataById: PropTypes.func.isRequired,
  updateTableData: PropTypes.func.isRequired,
  adjustBalance: PropTypes.func.isRequired,
  enableLoader: PropTypes.func.isRequired,
  exportToCSV: PropTypes.func.isRequired,
  twoFactorAuthentication: PropTypes.bool.isRequired,
  resetTwoFactorAuthentication: PropTypes.func.isRequired,
};
const mapStateToProps = createStructuredSelector({
  adjustments: makeSelectAdjustments(),
  twoFactorAuthentication: twoFactorAuthenticationSelector(),
});

export function mapDispatchToProps(dispatch) {
  return {
    searchAdjustments: data => dispatch(searchAdjustmentsAction(data)),
    resetFilters: () => dispatch(resetFiltersAction()),
    updateFilters: data => dispatch(updateFiltersAction(data)),
    resetPage: () => dispatch(resetPageAction()),
    vendorDataById: data => dispatch(vendorDataByIdAction(data)),
    updateTableData: data => dispatch(updateTableDataAction(data)),
    adjustBalance: data => dispatch(adjustBalanceAction(data)),
    enableLoader: () => dispatch(enableLoaderAction()),
    exportToCSV: data => dispatch(exportToCSVAction(data)),
    resetTwoFactorAuthentication: data =>
      dispatch(resetTwoFactorAuthenticationValue(data)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(Adjustments);
